import { useEffect, useRef } from 'react'
import {
  type CbamSimulationClarityEvent,
  trackCbamSimulationSignal
} from '@/features/cbam-simulations-v2/analytics/cbamSimulationClarity'

/**
 * Fires once per component mount. Pass a stable string literal for `event`.
 * In React StrictMode (development), the effect can still run twice because the ref resets on remount.
 */
export function useTrackCbamSimulationClarityOnceOnMount(event: CbamSimulationClarityEvent): void {
  const fired = useRef(false)
  useEffect(() => {
    if (fired.current) {
      return
    }
    fired.current = true
    trackCbamSimulationSignal(event)
  }, [event])
}

export function useTrackCbamSimulationListViewedOnce(): void {
  useTrackCbamSimulationClarityOnceOnMount('simulations:list:views')
}

export function useTrackCbamSimulationDetailsViewedWhenReady({
  cbamSimulationId,
  areResourcesLoading,
  hasSimulationData
}: {
  cbamSimulationId: string
  areResourcesLoading: boolean
  hasSimulationData: boolean
}): void {
  const lastFiredForIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (areResourcesLoading || !hasSimulationData) {
      return
    }
    if (lastFiredForIdRef.current === cbamSimulationId) {
      return
    }
    lastFiredForIdRef.current = cbamSimulationId
    trackCbamSimulationSignal('simulations:details:viewed')
  }, [areResourcesLoading, hasSimulationData, cbamSimulationId])
}
